import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { IotAsset } from 'src/app/core/models/iotAssetsModel.data';
import { Store } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, take, switchMap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { ActiaMapService } from 'src/app/core/services/actia-map.service';
import { MapContainerService } from 'src/app/core/services/mapContainer.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { IotAssetsService } from 'src/app/core/services/iotAssets.service';
import { IotAssetState } from 'src/app/core/store/iotAssets/iotAsset.reducer';
import { AppStateInterface } from 'src/app/appState.interface';
import * as IotAssetSelectors from 'src/app/core/store/iotAssets/iotAsset.selector';
import { AssetMappingConfig, AssetFieldConfig, findFieldConfigByName, extractNameFromFieldConfigKey, getFieldConfig, buildFieldConfigKey } from 'src/app/core/models/asset-config.model';
import { LoggerService } from 'src/app/core/services/logger.service';
import { CatalogService } from 'src/app/core/services/catalog.service';
import { CatalogResponseV2, MergedField } from 'src/app/core/models/catalog.model';
import { getApplySelectedCustomer } from 'src/app/core/store/selectors';


@Component({
  selector: 'app-actia-vehicle-details',
  templateUrl: './actia-vehicle-details.component.html',
  styleUrls: ['./actia-vehicle-details.component.scss']
})
export class ActiaVehicleDetailsComponent implements OnInit, OnDestroy {
  @Input()
  vehicle!: IotAsset; // Dati del veicolo da mostrare
  @Output() closePanel = new EventEmitter<void>(); // Evento per chiudere il pannello

  private destroy$ = new Subject<void>();

  // Observable per la configurazione
  mappingConfig$: Observable<AssetMappingConfig | null>;
  configLoading$: Observable<boolean>;
  configError$: Observable<any>;

  selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';
  private mergedFieldsMap: { [key: string]: MergedField } = {};
  private catalogVisibleFieldNames = new Set<string>();

  // Rimosse proprietà edit mode - gestiamo tutto centralmente

  constructor(
    private mapContainerService: MapContainerService,
    public mapService: ActiaMapService,
    public utlService: UtilsService,
    private store: Store<AppStateInterface & { iotAssets: IotAssetState }>,
    private translateService: TranslateService,
    private logger: LoggerService,
    private iotAssetsService: IotAssetsService,
    private catalogService: CatalogService
  ) {
    // Inizializza gli observable
    this.mappingConfig$ = this.store.select(IotAssetSelectors.selectMappingConfig);
    this.configLoading$ = this.store.select(IotAssetSelectors.selectConfigLoading);
    this.configError$ = this.store.select(IotAssetSelectors.selectConfigError);
  }
  @Input() visibleTabs: string[] = []; // Array di tab visibili
  @ViewChild('tabGroup') tabGroup!: MatTabGroup; // Riferimento al MatTabGroup
    selectedTabIndex = 0; // Imposta Summary come default
  mainColor: string = ''; // Colore principale del customer


  ngOnInit(): void {
    // console.log('Dati veicolo:', this.vehicle); // Log dei dati del veicolo
    this.mainColor = this.utlService.getMainColor();
    this.calculateSelectedTabIndex();

    // Carica catalog v2 e ricostruisce mergedFieldsMap ad ogni cambio di mappingConfig
    this.store.select(getApplySelectedCustomer).pipe(
      take(1),
      switchMap(customer =>
        customer?.uid
          ? this.catalogService.getCatalogForCustomer(customer.uid)
          : of({ data: [], result: '', errorDescription: null } as CatalogResponseV2)
      ),
      switchMap(catalog =>
        this.mappingConfig$.pipe(
          map(config => ({ catalog, config }))
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe(({ catalog, config }) => {
      this.catalogVisibleFieldNames = new Set(
        catalog.data.filter(i => i.visible !== false).map(i => i.name)
      );
      const fields = this.catalogService.buildMapConfigFields(catalog.data, config);
      // Indicizzata per "scope|name": lo stesso nome tecnico può esistere in scope diversi
      // (es. segnale Can Map e Dato di lavoro personalizzato omonimi) con etichette diverse.
      // L'entry per solo nome resta come fallback per chi non conosce lo scope.
      this.mergedFieldsMap = {};
      Object.values(fields).forEach(scopeFields =>
        scopeFields.forEach(f => {
          this.mergedFieldsMap[buildFieldConfigKey(f.scope, f.key)] = f;
          if (!this.mergedFieldsMap[f.key]) this.mergedFieldsMap[f.key] = f;
        })
      );
    });

    // Sottoscrivi ai cambiamenti della configurazione
    this.mappingConfig$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(config => {
      if (config) {
        this.logger.debug('vehicle-details', 'Elenco campi passato al componente', Object.keys(config.fields));
        // console.log('Configurazione mapping caricata:', config);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isCatalogVisible(fieldName: string): boolean {
    return this.catalogVisibleFieldNames.size === 0 || this.catalogVisibleFieldNames.has(fieldName);
  }

  // Ottieni la visibilità di un campo dalla configurazione store
  getFieldVisibility(fieldName: string): boolean {
    if (!this.isCatalogVisible(fieldName)) return false;
    let isVisible = false;
    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      const fieldConfig = findFieldConfigByName(config, fieldName);
      if (fieldConfig) {
        isVisible = fieldConfig.visible !== false;
      }
    });
    return isVisible;
  }

  // Verifica se un campo è visibile nei dettagli secondo la configurazione
  isFieldVisible(fieldName: string): boolean {
    if (!this.isCatalogVisible(fieldName)) return false;
    let isVisible = false;

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      const fieldConfig = findFieldConfigByName(config, fieldName);
      if (fieldConfig) {
        isVisible = fieldConfig.visible !== false && fieldConfig.showInDetails === true;
      }
    });

    return isVisible;
  }

  // Ottieni i campi per una sezione specifica dalla configurazione NgRx
  getFieldsForSection(sectionName: string): string[] {
    let fields: string[] = [];

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      if (config && config.fields) {
        fields = Object.keys(config.fields)
          .map(storageKey => extractNameFromFieldConfigKey(storageKey))
          .filter(fieldName => this.determineFieldSection(fieldName) === sectionName);
      }
    });

    return fields;
  }

  // Determina la sezione di un campo basandosi sulla configurazione (allineato con settings panel)
  private determineFieldSection(fieldName: string): string {
    let scope = 'other'; // default

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      const fieldConfig = findFieldConfigByName(config, fieldName);
      if (fieldConfig) {
        scope = fieldConfig.scope || 'other';
      }
    });

    return scope;
  }

  public generateFieldLabel(fieldName: string, scope?: string): string {
    const scoped = scope ? this.mergedFieldsMap[buildFieldConfigKey(scope, fieldName)] : undefined;
    return scoped?.label || this.mergedFieldsMap[fieldName]?.label || fieldName;
  }

  /**
   * Ottiene il tipo di dato dalla configurazione mappingConfig
   * @param fieldName Nome del campo
   * @returns Il tipo originale dal catalogo (Number, String, boolean, datetime, etc.)
   */
  public getFieldType(fieldName: string): string {
    let fieldType = 'String'; // Default

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      const fieldConfig = findFieldConfigByName(config, fieldName);
      if (fieldConfig) {
        // Usa catalogType se disponibile (tipo originale dal catalogo)
        // altrimenti usa type (tipo mappato)
        fieldType = fieldConfig.catalogType || fieldConfig.type;
      }
    });

    return fieldType;
  }

  /**
   * Ottiene l'unità di misura dalla configurazione mappingConfig
   * @param fieldName Nome del campo
   * @returns L'unità di misura (es. km/h, %, L)
   */
  public getFieldUnit(fieldName: string): string {
    let fieldUnit = ''; // Default

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      const fieldConfig = findFieldConfigByName(config, fieldName);
      if (fieldConfig) {
        fieldUnit = fieldConfig.unit || '';
      }
    });

    return fieldUnit;
  }

  /**
   * Formatta un valore in base al suo tipo
   * @param value Valore da formattare
   * @param fieldName Nome del campo
   * @returns Valore formattato
   */
  public formatFieldValue(value: any, fieldName: string): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const type = this.getFieldType(fieldName);

    switch (type.toLowerCase()) {
      case 'number':
      case 'double':
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return '-';
        }
        return numValue.toString();

      case 'int':
      case 'integer':
        const intValue = parseInt(value);
        if (isNaN(intValue)) {
          return '-';
        }
        return intValue.toString();

      case 'boolean':
      case 'bool':
        // Cerca traduzione specifica per questo campo
        const boolValue = value === true || value === 'true' || value === 1 || value === '1';
        const trueKey = `catalog.signals.${fieldName}.valueTrue`;
        const falseKey = `catalog.signals.${fieldName}.valueFalse`;
        const trueLabel = this.translateService.instant(trueKey);
        const falseLabel = this.translateService.instant(falseKey);

        // Se esistono traduzioni specifiche, usale
        if (trueLabel !== trueKey && falseLabel !== falseKey) {
          return boolValue ? trueLabel : falseLabel;
        }

        // Altrimenti usa Sì/No come default
        return boolValue ? 'Sì' : 'No';

      case 'datetime':
      case 'date':
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return value.toString();
          }
          return date.toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch {
          return value.toString();
        }

      case 'string':
      default:
        return value.toString();
    }
  }

  /**
   * Ottiene il valore formattato con l'unità di misura
   * @param value Valore da formattare
   * @param fieldName Nome del campo
   * @returns Valore formattato con unità
   */
  public getFormattedValueWithUnit(value: any, fieldName: string): string {
    const formattedValue = this.formatFieldValue(value, fieldName);
    const unit = this.getFieldUnit(fieldName);

    if (unit && formattedValue !== '-') {
      return `${formattedValue} ${unit}`;
    }

    return formattedValue;
  }

  // Ottieni tutti i campi visibili raggruppati per sezione
  getVisibleFieldsBySection(): { [section: string]: Array<{ name: string; label: string; value: any }> } {
    const result: { [section: string]: Array<{ name: string; label: string; value: any }> } = {};

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      if (config && config.fields) {
        Object.keys(config.fields).forEach(storageKey => {
          const fieldConfig = config.fields[storageKey];
          const fieldName = extractNameFromFieldConfigKey(storageKey);

          // Solo campi visibili e visibili nel catalogo
          if (fieldConfig.visible && this.isCatalogVisible(fieldName)) {
            const section = this.determineFieldSection(fieldName);

            if (!result[section]) {
              result[section] = [];
            }

            result[section].push({
              name: fieldName,
              label: fieldConfig.label || this.generateFieldLabel(fieldName, fieldConfig.scope),
              value: this.getFormattedValueWithUnit(this.vehicle[fieldName], fieldName)
            });
          }
        });
      }
    });

    return result;
  }

  /**
   * Ottieni i campi organizzati per scope
   * @returns Array di oggetti con scope e relativi campi
   */
  getFieldsByScope(): Array<{ scope: string; fields: string[] }> {
    const result: Array<{ scope: string; fields: string[] }> = [];
    const scopeMap: { [scope: string]: string[] } = {};

    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      if (config && config.fields) {
        // Ottieni solo i campi visibili E che devono essere mostrati nei dettagli E compatibili con il can_map_name del veicolo
        const visibleEntries = Object.keys(config.fields)
          .map(storageKey => ({
            fieldName: extractNameFromFieldConfigKey(storageKey),
            fieldConfig: config.fields[storageKey]
          }))
          .filter(({ fieldName, fieldConfig }) => {
            const basicVisibility = fieldConfig.visible !== false &&
              fieldConfig.showInDetails === true &&
              this.isCatalogVisible(fieldName);

            if (!basicVisibility) return false;

            // Se non abbiamo can_map_name del veicolo, usa solo la visibilità base
            if (!this.vehicle?.can_map_name) {
              return true;
            }

            // Applica il filtering per can_map_name
            return this.iotAssetsService.isFieldVisibleForVehicle(fieldName, this.vehicle.can_map_name);
          });

        this.logger.debug('vehicle-details', `Campi filtrati per dettagli: ${visibleEntries.length} su ${Object.keys(config.fields).length}`);

        // Raggruppa per scope usando la configurazione (allineato con settings panel)
        visibleEntries.forEach(({ fieldName, fieldConfig }) => {
          const scope = fieldConfig.scope || 'other';

          if (!scopeMap[scope]) {
            scopeMap[scope] = [];
          }
          scopeMap[scope].push(fieldName);
        });

        const sortFieldsInScope = (scope: string): string[] =>
          scopeMap[scope].sort((a, b) => {
            // Prima ordina per displayOrder se presente, poi per label alfabetico
            const fieldConfigA = getFieldConfig(config, scope, a);
            const fieldConfigB = getFieldConfig(config, scope, b);
            const orderA = fieldConfigA?.displayOrder ?? 999999;
            const orderB = fieldConfigB?.displayOrder ?? 999999;

            if (orderA !== orderB) {
              return orderA - orderB;
            }

            return this.generateFieldLabel(a, scope).localeCompare(this.generateFieldLabel(b, scope));
          });

        // Ordina gli scope usando l'ordine salvato dal settings panel
        const orderedScopes = this.getScopeOrder(scopeMap);
        orderedScopes.forEach(scope => {
          if (scopeMap[scope] && scopeMap[scope].length > 0) {
            result.push({ scope, fields: sortFieldsInScope(scope) });
          }
        });

        // Aggiungi altri scope non standard
        Object.keys(scopeMap).forEach(scope => {
          if (!orderedScopes.includes(scope)) {
            result.push({ scope, fields: sortFieldsInScope(scope) });
          }
        });
      }
    });

    return result;
  }

  /**
   * Ottieni il nome visualizzato per uno scope (allineato con settings panel)
   * @param scope Identificativo dello scope
   * @returns Nome leggibile dello scope
   */
  getScopeDisplayName(scope: string): string {
    return this.catalogService.getScopeLabel(scope);
  }


   // Metodo per verificare se un tab è visibile
   isTabVisible(tabName: string): boolean {
    return this.visibleTabs.includes(tabName);
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes['vehicle'] && !changes['vehicle'].firstChange) {
  //     console.log('Dati veicolo cambiati:', changes['vehicle'].currentValue);
  //   }
  // }

// Non servono importazioni aggiuntive per Material Icons

  // calculateJourneyDuration(): string {
  //   if (!this.vehicle?.properties?.vehicle?.location?.start?.timestamp ||
  //       !this.vehicle?.properties?.vehicle?.location?.current?.timestamp) {
  //     return 'N/A';
  //   }

  //   const startTime = new Date(this.vehicle.properties.vehicle.location.start.timestamp).getTime();
  //   const currentTime = new Date(this.vehicle.properties.vehicle.location.current.timestamp).getTime();

  //   const durationInMs = currentTime - startTime;

  //   if (durationInMs < 0) {
  //     return 'Invalid timestamps';
  //   }

  //   const seconds = Math.floor((durationInMs / 1000) % 60);
  //   const minutes = Math.floor((durationInMs / (1000 * 60)) % 60);
  //   const hours = Math.floor(durationInMs / (1000 * 60 * 60));

  //   return `${hours}h ${minutes}m ${seconds}s`;
  // }

  onTabChange(event: MatTabChangeEvent): void {
    // console.log('Tab cambiata:', event);

    // Verifica dinamicamente il numero di tab disponibili
    const tabCount = this.tabGroup._tabs.length;
    const targetIndex = tabCount === 3 ? 2 : 1;

    // Esegui azioni specifiche in base alla tab selezionata
    if (event.index === targetIndex) {
      this.mapContainerService.setActivePage('actia-journey'); // Imposta la pagina attiva su "actia-journey"
   // Imposta la tab "Summary" come predefinita
   setTimeout(() => {
    this.tabGroup.selectedIndex = 0; // Indice della tab "Summary"
  }, 0);
    }
  }


  // getStatusFrom(){

  //     const permanenza_stato =
  //       this.vehicle.updated_at && this.vehicle.status_entered_at
  //         ? Math.abs(
  //             new Date(this.vehicle.updated_at).getTime() -
  //               new Date(this.vehicle.status_entered_at).getTime()
  //           ) // Differenza in millisecondi
  //         : 0; // Se una delle due date è null o undefined, imposta a 0

  //     const permanenza_stato_in_ore = Math.floor(
  //       permanenza_stato / (1000 * 60 * 60)
  //     ); // Differenza in ore
  //     const permanenza_stato_in_minuti = Math.floor(
  //       (permanenza_stato % (1000 * 60 * 60)) / (1000 * 60)
  //     ); // Minuti rimanenti

  //     const permanenza_formattata =
  //       permanenza_stato_in_ore > 0
  //         ? `${permanenza_stato_in_ore}h:${permanenza_stato_in_minuti} min`
  //         : `${permanenza_stato_in_minuti} min`;
  //         return permanenza_formattata;
  // }

  getStatusFrom(): string {
  if (!this.vehicle?.status_entered_at) {
    return 'N/A';
  }

  const enteredAt = new Date(this.vehicle.status_entered_at).getTime();
  const now = Date.now();
const permanenza_stato = this.vehicle.fromtime_last_update_status ?? 0; // valore in secondi

const permanenza_stato_in_ore = Math.floor(permanenza_stato / 3600); // Ore
const permanenza_stato_in_minuti = Math.floor((permanenza_stato % 3600) / 60); // Minuti rimanenti

const permanenza_formattata =
  permanenza_stato_in_ore > 0
    ? `${permanenza_stato_in_ore}h:${permanenza_stato_in_minuti} min`
    : `${permanenza_stato_in_minuti} min`;

  return permanenza_formattata;
}



   private calculateSelectedTabIndex() {
    const tabs = ['Summary', 'Journey', 'Driver', 'Others'];
    let visibleIndex = 0;

    for (let i = 0; i < tabs.length; i++) {
      const tabName = tabs[i];
      if (tabName === 'Summary') {
        // Trovato l'indice di Summary tra le tab visibili
        this.selectedTabIndex = visibleIndex;
        return;
      }
      if (this.isTabVisible(tabName)) {
        visibleIndex++;
      }
    }
  }

  /**
   * Restituisce il numero totale di campi visibili
   */
  getTotalVisibleFieldsCount(): number {
    let fieldCount = 0;
    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      if (config && config.fields) {
        fieldCount = Object.keys(config.fields).filter(storageKey =>
          config.fields[storageKey].visible !== false && this.isCatalogVisible(extractNameFromFieldConfigKey(storageKey))
        ).length;
      }
    });
    return fieldCount;
  }

  // Metodi edit mode rimossi - gestione centralizzata

  /**
   * Restituisce solo i campi visibili per la vista normale
   */
  getAllVisibleFields(): string[] {
    let visibleFields: string[] = [];
    this.mappingConfig$.pipe(take(1)).subscribe(config => {
      if (config && config.fields) {
        visibleFields = Object.keys(config.fields)
          .filter(storageKey => config.fields[storageKey].visible)
          .map(storageKey => extractNameFromFieldConfigKey(storageKey))
          .filter(fieldName => this.isCatalogVisible(fieldName));
      }
    });
    return visibleFields;
  }

  /**
   * Ottiene l'ordine degli scope dal localStorage, con fallback all'ordine di default
   */
  private getScopeOrder(scopeMap: { [scope: string]: string[] }): string[] {
    try {
      // Carica l'ordine salvato dal settings panel
      const saved = localStorage.getItem('settings-panel-scope-order');
      if (saved) {
        const savedOrder = JSON.parse(saved);
        // Verifica che gli scope salvati siano ancora validi e presenti nei dati
        const validScopes = savedOrder.filter((scope: string) =>
          scopeMap[scope] && scopeMap[scope].length > 0
        );

        // Aggiungi eventuali nuovi scope alla fine
        const allScopes = Object.keys(scopeMap);
        const newScopes = allScopes.filter(scope => !validScopes.includes(scope));

        return [...validScopes, ...newScopes];
      }
    } catch (error) {
      this.logger.error('vehicle-details', 'Errore nel caricamento ordine scope:', error);
    }

    // Fallback all'ordine di default; gli scope non previsti (es. kpi, kpi_custom, erp) vanno in coda,
    // non vanno persi
    const defaultOrder = ['signal', 'platform', 'tcu', 'erp', 'kpi', 'kpi_custom', 'common', 'model', 'other'];
    const present = Object.keys(scopeMap).filter(scope => scopeMap[scope] && scopeMap[scope].length > 0);
    const ordered = defaultOrder.filter(scope => present.includes(scope));
    const remaining = present.filter(scope => !ordered.includes(scope));
    return [...ordered, ...remaining];
  }

}
